/**
 * Pure distance->volume math for positional sound, modelled on ClassicUO's
 * `AudioManager.PlaySoundWithDistance` (see ADR-0092) but reduced to the part this
 * walk-around needs. A sound has a world tile (the stepping mobile, the toggled
 * door); the client scales its volume by how far that tile is from the local
 * player, so someone walking past you is loud and someone across the bank is faint.
 *
 * No Pixi, no DOM, no audio API, no wall clock - just arithmetic, so it is unit
 * tested directly.
 */

/** Integer tile coordinates. */
export interface Tile {
  x: number;
  y: number;
}

/**
 * Chebyshev (king-move) tile distance, exactly the metric ClassicUO measures sound
 * range with (`max(|dx|, |dy|)`): a diagonal neighbour is distance 1, matching how
 * UO's 8-way movement and view range work.
 */
export function tileDistance(a: Tile, b: Tile): number {
  return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));
}

/**
 * Volume for a sound `distance` tiles from the listener, in `[0, base]`:
 *
 *  - `base` at distance 0 (on top of the listener - e.g. your own footsteps),
 *  - a linear falloff of `base / (maxTiles + 1)` per tile, matching ClassicUO's
 *    per-tile decrement, so it reaches ~0 right at the edge of audible range,
 *  - hard 0 beyond `maxTiles` (out of earshot), so a distant event is dropped
 *    entirely rather than played at a hair of volume.
 *
 * `base` is the sound's own loudness ceiling (footsteps quieter than doors);
 * `maxTiles` is the audible radius (UO's client view range, ~18 tiles).
 */
export function proximityVolume(distance: number, maxTiles: number, base: number): number {
  if (distance > maxTiles) return 0;
  if (distance <= 0) return base;
  return base * (1 - distance / (maxTiles + 1));
}
